import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useLanguage } from '../contexts/LanguageContext';
import { useLegal } from '../lib/legalStrings';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const CONFIRM_WORD = 'DELETE';

export default function DeleteAccountDialog({ isOpen, onClose }) {
  const { language } = useLanguage();
  const L = useLegal(language);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);

  const canConfirm = confirmText.trim() === CONFIRM_WORD;

  const handleDelete = async () => {
    if (!canConfirm) return;
    setLoading(true);
    try {
      await api.delete('/auth/account');
      toast.success(L.settings.accountDeleted);
      // Clear local session and route home. `logout` swallows errors gracefully.
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to delete account';
      toast.error(msg);
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setConfirmText('');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={handleClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            className="relative bg-card border border-destructive/40 rounded-3xl p-6 max-w-md w-full shadow-2xl"
            data-testid="delete-account-dialog"
          >
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
              aria-label="Close"
              disabled={loading}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-destructive" aria-hidden="true" />
              </div>
              <div>
                <h2 className="font-heading text-lg font-bold">
                  {L.settings.deleteConfirmTitle}
                </h2>
              </div>
            </div>

            <p className="text-sm text-muted-foreground leading-relaxed mb-5">
              {L.settings.deleteConfirmBody}
            </p>

            <div className="space-y-2 mb-5">
              <Label htmlFor="delete-confirm-input" className="text-xs">
                {L.settings.deleteConfirmType}
              </Label>
              <Input
                id="delete-confirm-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={CONFIRM_WORD}
                autoComplete="off"
                data-testid="delete-confirm-input"
                disabled={loading}
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleClose}
                disabled={loading}
                data-testid="delete-cancel"
              >
                {L.settings.deleteCancel}
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleDelete}
                disabled={!canConfirm || loading}
                data-testid="delete-confirm"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  L.settings.deleteConfirmBtn
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
